/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlProps,
    LinearProgress,
    MenuItem,
    TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {Field, FieldProps, Form, Formik} from 'formik';
import {useSnackbar} from 'notistack';
import FormikTextField from 'components/atoms/FormikInput/FormikInput';
import {OrderDataUpdatePut, updateOrderData2} from 'api/orders';
import {DictionaryDto} from 'api/dictionary';
import {fetchRegionsDict} from 'api/regions';
import FormikSelectControl from 'components/atoms/FormikSelect/FormicSelectControl';
import {PostAddress} from 'api/types/PostAddress';
import {getPostAddress} from '../../../api/address';
import {PostAddressDialog} from '../PostAddressDialog';
import {CustomPostAddress} from '../../../api/types/CustomPostAddress';
import {Props} from './props';
import {useStyles} from './styles';

export const OrderEditDialog: React.FC<Props> = ({open, onClose, order}) => {
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const [regions, setRegions] = useState<DictionaryDto[]>([]);
    const [addresses, setAddresses] = useState<PostAddress[]>([]);
    const [addressType, setAddressType] = useState<'DICTIONARY' | 'MANUAL' | ''>('');
    const [autocompleteOpen, setAutocompleteOpen] = React.useState(false);
    const [autocompleteLoading, setAutocompleteLoading] = useState(false);
    const [addressDialogOpen, setAddressDialogOpen] = useState(false);

    const [orderForm, setOrderFrom] = useState<OrderDataUpdatePut>({
        id: order.id,
        order_comment: '',
        region_code: order.region.code,
        weight: order.order_detail.weight,
        source_address: {
            address_rus: order.order_address?.source?.address || '',
            postcode: order.order_address?.source?.new_postal_code || '',
            full_address: {
                old_postcode: order.order_address?.source?.postal_code || '',
            },
        },
        source_address_dict: {
            address_rus: '',
            postcode: '',
            full_address: {
                old_postcode: '',
            },
        },
    });

    const getAddresses = useCallback(async (address: string) => {
        try {
            setAutocompleteLoading(true);
            if (address.length > 0) {
                const res = await getPostAddress(address);
                setAddresses(res);
            }
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении адресов: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setAutocompleteLoading(false);
        }
    }, []);

    const handleAddressTypeChange = (type: 'DICTIONARY' | 'MANUAL' | '') => {
        setAddresses([]);
        setAddressType(type);
    };

    const load = useCallback(async () => {
        try {
            setRegions(await fetchRegionsDict());
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        }
    }, [enqueueSnackbar]);

    const handleAddressDialogClose = useCallback((data?: CustomPostAddress) => {
        if (data) {
            setOrderFrom({
                ...orderForm,
                source_address: {
                    address_rus: data.address,
                    postcode: data.new_post_code,
                    full_address: {
                        old_postcode: data.old_post_code,
                    },
                },
            });
        }

        setAddressDialogOpen(false);
    }, []);

    useEffect(() => {
        load();
    }, []);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Редактирование данных заявки</DialogTitle>
            <DialogContent>
                <Formik
                    enableReinitialize={true}
                    initialValues={orderForm}
                    onSubmit={async (values, {setSubmitting}) => {
                        setSubmitting(true);
                        try {
                            await updateOrderData2(values);
                            enqueueSnackbar('Данные успешно сохранены', {variant: 'success'});
                            onClose();
                        } catch (e) {
                            enqueueSnackbar(
                                `Ошибка при сохранении данных: ${e.response?.data?.message || e}`,
                                {
                                    variant: 'error',
                                }
                            );
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({isSubmitting}) => (
                        <Form>
                            <Box display="flex" flexDirection="column">
                                <Field name="region_code">
                                    {({...props}) => (
                                        <FormikSelectControl
                                            cls={classes.mt2}
                                            label="Регион"
                                            labelId="reg"
                                            {...props}
                                        >
                                            {regions.map((doc, index) => (
                                                <MenuItem key={index} value={doc.code}>
                                                    {doc.description}
                                                </MenuItem>
                                            ))}
                                        </FormikSelectControl>
                                    )}
                                </Field>
                                <Field
                                    className={classes.mt2}
                                    component={FormikTextField}
                                    name="weight"
                                    label="Вес"
                                />
                                <Field
                                    className={classes.mt2}
                                    component={FormikTextField}
                                    name="order_comment"
                                    label="Комментарий"
                                />
                                <Field
                                    className={classes.mt2}
                                    component={FormikTextField}
                                    name="source_address.postcode"
                                    label="Новый почтовый индекс"
                                    disabled
                                />
                                <Field
                                    className={classes.mt2}
                                    component={FormikTextField}
                                    name="source_address.full_address.old_postcode"
                                    label="Старый почтовый индекс"
                                    disabled
                                />
                                <Field
                                    className={classes.mt2}
                                    component={FormikTextField}
                                    name="source_address.address_rus"
                                    label="Адрес"
                                    disabled
                                />
                                <ButtonGroup
                                    color="primary"
                                    aria-label="contained primary button group"
                                    className={classes.mt2}
                                >
                                    <Button
                                        variant={
                                            addressType === 'DICTIONARY' ? 'contained' : 'outlined'
                                        }
                                        onClick={() => handleAddressTypeChange('DICTIONARY')}
                                    >
                                        Из справочников
                                    </Button>
                                    <Button
                                        variant={
                                            addressType === 'MANUAL' ? 'contained' : 'outlined'
                                        }
                                        onClick={() => {
                                            handleAddressTypeChange('MANUAL');
                                            setAddressDialogOpen(true);
                                        }}
                                    >
                                        Вручную
                                    </Button>
                                </ButtonGroup>
                                {addressType === 'DICTIONARY' && (
                                    <Field name="source_address_dict">
                                        {({
                                              form,
                                              field,
                                          }: FieldProps &
                                            React.PropsWithChildren<any> &
                                            FormControlProps) => {
                                            return (
                                                <Autocomplete
                                                    id="source-address"
                                                    className={classes.mt2}
                                                    open={autocompleteOpen}
                                                    onOpen={() => {
                                                        setAutocompleteOpen(true);
                                                    }}
                                                    value={field.value}
                                                    onClose={() => {
                                                        setAutocompleteOpen(false);
                                                    }}
                                                    onChange={(_e, v: PostAddress | null, _r) => {
                                                        form.setFieldValue('source_address', {
                                                            postcode: v?.postcode,
                                                            address_rus: v?.address_rus,
                                                            full_address: {
                                                                old_postcode:
                                                                v?.full_address?.old_postcode,
                                                            },
                                                        });
                                                    }}
                                                    // getOptionSelected={(
                                                    //     option: PostAddress,
                                                    //     value: PostAddress
                                                    // ) => option.postcode === value.postcode}
                                                    getOptionLabel={(option: PostAddress) =>
                                                        option?.address_rus
                                                    }
                                                    options={addresses}
                                                    loading={
                                                        autocompleteOpen && autocompleteLoading
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Выберите адрес"
                                                            variant="outlined"
                                                            onChange={(e) => {
                                                                getAddresses(e.target.value);
                                                            }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                endAdornment: (
                                                                    <React.Fragment>
                                                                        {autocompleteLoading ? (
                                                                            <CircularProgress
                                                                                color="inherit"
                                                                                size={20}
                                                                            />
                                                                        ) : null}
                                                                        {
                                                                            params.InputProps
                                                                                .endAdornment
                                                                        }
                                                                    </React.Fragment>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            );
                                        }}
                                    </Field>
                                )}
                                {addressType === 'MANUAL' && (
                                    <PostAddressDialog
                                        onClose={handleAddressDialogClose}
                                        receiptDate={order?.date_created}
                                        orderNumber={order?.order_number}
                                        postCode={order.order_address?.source?.postal_code}
                                        regionDescription={order.region.description}
                                        open={addressDialogOpen}
                                    />
                                )}
                            </Box>

                            <Box>{isSubmitting && <LinearProgress/>}</Box>
                            <Box marginTop={3} display="flex" justifyContent="flex-end">
                                <Button onClick={onClose}>Отмена</Button>
                                <Button type={'submit'}>Ок</Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default OrderEditDialog;
